import React from 'react'
import {
  Table,
  Box,
  Text,
  HStack,
  Button,
  Icon,
} from '@chakra-ui/react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

interface Column<T> {
  key: string
  label: string
  render?: (item: T) => React.ReactNode
  sortable?: boolean
}

interface DataTableProps<T> {
  data: T[]
  columns: Column<T>[]
  onRowClick?: (item: T) => void
  isLoading?: boolean
  emptyMessage?: string
}

export function DataTable<T extends { id?: string }>({
  data,
  columns,
  onRowClick,
  isLoading = false,
  emptyMessage = 'No data available',
}: DataTableProps<T>) {
  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.500">Loading...</Text>
      </Box>
    )
  }

  if (data.length === 0) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.500">{emptyMessage}</Text>
      </Box>
    )
  }

  return (
    <Box overflowX="auto">
      <Table.Root size="sm">
        <Table.Header>
          <Table.Row>
            {columns.map((column) => (
              <Table.ColumnHeader key={column.key}>{column.label}</Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((item, index) => (
            <Table.Row
              key={item.id || index}
              onClick={() => onRowClick?.(item)}
              cursor={onRowClick ? 'pointer' : 'default'}
              _hover={onRowClick ? { bg: 'gray.50' } : {}}
            >
              {columns.map((column) => (
                <Table.Cell key={column.key}>
                  {column.render
                    ? column.render(item)
                    : (item as any)[column.key]}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  return (
    <HStack justify="center" gap={2} mt={4}>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onPageChange(currentPage - 1)}
        isDisabled={currentPage === 1}
        leftIcon={<Icon as={FiChevronLeft} />}
      >
        Previous
      </Button>
      <Text fontSize="sm" color="gray.600">
        Page {currentPage} of {totalPages}
      </Text>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onPageChange(currentPage + 1)}
        isDisabled={currentPage >= totalPages}
        rightIcon={<Icon as={FiChevronRight} />}
      >
        Next
      </Button>
    </HStack>
  )
}

